import { onValue, remove, set, update } from 'firebase/database';
import { firebase } from '../config/configureFirebase';
import store from '../store/store';

const adminFetchHost = () => {
  const { config } = firebase;
  const settings = store.getState().settingsdata.settings;
  if (window && window.location && settings.CompanyWebsite === window.location.origin) {
    return window.location.origin;
  }
  return `https://${config.projectId}.web.app`;
};

async function postFareMarketAdvisor(body) {
  const { auth } = firebase;
  const idToken = await auth.currentUser.getIdToken();
  const response = await fetch(`${adminFetchHost()}/fareMarketAdvisorRun`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  return response.json();
}

export const fetchFareRecommendations = () => async (dispatch) => {
  const { fareRecommendationsRef, config } = firebase;
  dispatch({ type: 'FETCH_FARE_RECOMMENDATIONS', payload: null });
  onValue(fareRecommendationsRef, (snapshot) => {
    if (!snapshot.val()) {
      dispatch({ type: 'FETCH_FARE_RECOMMENDATIONS_SUCCESS', payload: [] });
      return;
    }
    const data = snapshot.val();
    const list = Object.keys(data)
      .filter((key) => key !== 'last_applied')
      .map((key) => ({ ...data[key], id: key, cityId: data[key].cityId || key }))
      .sort((a, b) => (b.appliedAt || b.createdAt || 0) - (a.appliedAt || a.createdAt || 0));
    dispatch({
      type: 'FETCH_FARE_RECOMMENDATIONS_SUCCESS',
      payload: list,
    });
  }, (error) => {
    dispatch({
      type: 'FETCH_FARE_RECOMMENDATIONS_FAILED',
      payload: error?.message || 'Error al cargar recomendaciones.',
    });
  });
  return config;
};

/** Solo admin: calcula y activa tarifas de vehículos activos (home + app). */
export const runFareMarketAdvisorNow = async (options = {}) => {
  try {
    const result = await postFareMarketAdvisor({
      action: options.dryRun ? 'analyze' : 'run',
      dryRun: options.dryRun === true,
    });
    if (result.success) return result;
    return { success: false, error: result.error || 'No se pudo ejecutar el cálculo inteligente.' };
  } catch (error) {
    return { success: false, error: error?.message || 'No se pudo ejecutar el cálculo inteligente.' };
  }
};

export const applyFareRecommendationCity = async (cityId) => {
  try {
    const result = await postFareMarketAdvisor({ action: 'apply', cityId });
    if (result.success) return result;
    return { success: false, error: result.error || 'No se pudo aplicar la recomendación.' };
  } catch (error) {
    return { success: false, error: error?.message || 'No se pudo aplicar la recomendación.' };
  }
};

export const dismissFareRecommendation = async (cityId) => {
  const { fareRecommendationEditRef } = firebase;
  await update(fareRecommendationEditRef(cityId), {
    status: 'dismissed',
    dismissedAt: Date.now(),
  });
  return { success: true };
};

export const fetchFareMarketBenchmarks = () => (dispatch) => {
  const { fareMarketBenchmarksRef } = firebase;
  dispatch({ type: 'FETCH_FARE_BENCHMARKS', payload: null });
  onValue(
    fareMarketBenchmarksRef,
    (snapshot) => {
      dispatch({
        type: 'FETCH_FARE_BENCHMARKS_SUCCESS',
        payload: snapshot.val() || {},
      });
    },
    (error) => {
      dispatch({
        type: 'FETCH_FARE_BENCHMARKS_FAILED',
        payload: error?.message || 'benchmarks_fetch_failed',
      });
    }
  );
};

/**
 * Guarda referencia de competencia para una ciudad + vehículo.
 * cityKey: cityId (preferido) o nombre de ciudad.
 * carKey: nombre del tipo (Taxi, C2…) — el asesor busca por id o name.
 */
export const saveFareMarketBenchmark = async ({
  cityKey,
  cityName,
  carKey,
  carTypeId,
  min_fare,
  rate_per_unit_distance,
  rate_per_hour,
  base_fare,
  source,
}) => {
  const { fareMarketBenchmarkCarRef } = firebase;
  const keyCity = String(cityKey || '').trim();
  const keyCar = String(carKey || '').trim();
  if (!keyCity || !keyCar) {
    return { success: false, error: 'Indica ciudad y vehículo.' };
  }
  const payload = {
    carTypeId: carTypeId || null,
    name: keyCar,
    cityName: cityName || null,
    min_fare: min_fare === '' || min_fare == null ? null : Number(min_fare),
    rate_per_unit_distance:
      rate_per_unit_distance === '' || rate_per_unit_distance == null
        ? null
        : Number(rate_per_unit_distance),
    // rate_per_hour = COP por minuto (T); nombre legado en RTDB.
    rate_per_hour:
      rate_per_hour === '' || rate_per_hour == null ? null : Number(rate_per_hour),
    base_fare: base_fare === '' || base_fare == null ? null : Number(base_fare),
    source: String(source || '').trim().slice(0, 200) || null,
    checkedAt: Date.now(),
  };
  await set(fareMarketBenchmarkCarRef(keyCity, keyCar), payload);
  return { success: true, payload };
};

export const deleteFareMarketBenchmark = async ({ cityKey, carKey }) => {
  const { fareMarketBenchmarkCarRef } = firebase;
  const keyCity = String(cityKey || '').trim();
  const keyCar = String(carKey || '').trim();
  if (!keyCity || !keyCar) {
    return { success: false, error: 'Indica ciudad y vehículo.' };
  }
  await remove(fareMarketBenchmarkCarRef(keyCity, keyCar));
  return { success: true };
};

/** Solo admin: lee la API key de DeepSeek desde admin_secrets (no /settings). */
export const fetchDeepseekApiKey = () => (dispatch) => {
  const { deepseekApiKeyRef } = firebase;
  dispatch({ type: 'FETCH_DEEPSEEK_API_KEY', payload: null });
  onValue(
    deepseekApiKeyRef,
    (snapshot) => {
      const val = snapshot.val();
      dispatch({
        type: 'FETCH_DEEPSEEK_API_KEY_SUCCESS',
        payload: typeof val === 'string' ? val : '',
      });
    },
    (error) => {
      dispatch({
        type: 'FETCH_DEEPSEEK_API_KEY_FAILED',
        payload: error?.message || 'deepseek_api_key_fetch_failed',
      });
    }
  );
};

/** Alias de compatibilidad (UI antigua). */
export const fetchGeminiApiKey = fetchDeepseekApiKey;

/** Solo admin: guarda la API key en admin_secrets/deepseekApiKey. */
export const editDeepseekApiKey = (apiKey) => async (dispatch) => {
  const { deepseekApiKeyRef } = firebase;
  const value = String(apiKey || '').trim();
  await set(deepseekApiKeyRef, value);
  dispatch({ type: 'FETCH_DEEPSEEK_API_KEY_SUCCESS', payload: value });
  return value;
};

/** Alias de compatibilidad (UI antigua). */
export const editGeminiApiKey = editDeepseekApiKey;
